import { useSnackbar } from 'notistack';
import { GET_PATIENTS, GET_PATIENTS_BASIC } from '../gqlSchemes/getPatients';
import { useQuery } from '@apollo/client';
import { useState } from 'react';

export default function useGetPatients({
  perPage = 20,
  filter = {},
  withCarePlan = true,
  setPaginationInfo,
  page = 0,
}) {
  const [patients, setPatients] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, fetchMore } = useQuery(GET_PATIENTS, {
    fetchPolicy: 'no-cache',
    variables: {
      page,
      withCarePlan,
      filter: {
        ...filter,
        // only keep id from careTeams
        ...(filter?.careTeamIds && {
          careTeamIds: filter.careTeamIds?.map((careTeam) => careTeam.id),
        }),
        // remove display from conditions object before sending to BE
        ...(filter?.condition && {
          condition: filter.condition?.map((condition) => ({
            code: condition.code,
            system: condition.system,
          })),
        }),
        // send only array of careplans to BE
        ...(filter?.activeRequestGroupPlanDefinitionIds && {
          activeRequestGroupPlanDefinitionIds: filter.activeRequestGroupPlanDefinitionIds?.map(
            (activePlanDefinitionId) => activePlanDefinitionId.id
          ),
        }),
        // send only array of careplans to BE
        ...(filter?.completedRequestGroupPlanDefinitionIds && {
          completedRequestGroupPlanDefinitionIds: filter.completedRequestGroupPlanDefinitionIds?.map(
            (retiredPlanDefinitionId) => retiredPlanDefinitionId.id
          ),
        }),
        statuses: ['ACTIVE', 'INACTIVE'],
        patientGroupIds: filter?.patientGroupIds?.map((i) => i.id),
      },
      perPage,
    },
    onCompleted: (res) => {
      if (res.user.patient.patientPagination.items) {
        setPatients(res.user.patient.patientPagination.items);
        setPaginationInfo && setPaginationInfo(res.user.patient.patientPagination.pageInfo);
      }
    },
  });

  return { patients, loading, error };
}

export function useGetPatientsForInfiniteScroll({
  filter = {},
}) {
  const [patients, setPatients] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    page: 0,
    perPage: 20,
    hasNextPage: true
  });

  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, fetchMore } = useQuery(GET_PATIENTS_BASIC, {
    fetchPolicy: 'no-cache',
    variables: {
      page: 0,
      filter: filter,
      perPage: 20,
    },
    onCompleted: (res) => {
      if (res.user.patient.patientPagination.items) {
        setPatients(res.user.patient.patientPagination.items);
        setPageInfo(res.user.patient.patientPagination.pageInfo);
      }
    },
  });

  const loadMore = () => {
    if (!pageInfo.hasNextPage) return;

    fetchMore({
      variables: {
        page: pageInfo.page + 1,
        perPage: pageInfo.perPage,
      },
      updateQuery: (previousData, { fetchMoreResult }) => {
        if (fetchMoreResult.user.patient.patientPagination.items) {
          setPatients((curr) => {
            const currentIds = new Set(curr.map(p => p.id));

            return [
              ...curr,
              ...fetchMoreResult.user.patient.patientPagination.items.filter(i => !currentIds.has(i.id))
            ]
          });
          setPageInfo(currentPageInfo => {
            const newPageInfo = fetchMoreResult.user.patient.patientPagination.pageInfo;

            return newPageInfo.page > currentPageInfo.page ? newPageInfo : currentPageInfo;
          });
        }
      },
    });
  };

  return { patients, loading, error, loadMore };
}